import { useState, useMemo, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Check, Users, BedDouble, Maximize, ArrowLeft, Star, Calendar } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { PageLoader } from "../components/Loader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckoutDialog } from "../components/CheckoutDialog";
import { InquiryDialog } from "../components/InquiryDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString()}`;

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

export default function PackageDetail() {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [activeImg, setActiveImg] = useState(0);
  const [roomId, setRoomId] = useState(null);
  const [checkIn, setCheckIn] = useState(iso(addDays(new Date(), 21)));
  const [checkOut, setCheckOut] = useState("");
  const [travelers, setTravelers] = useState(2);
  const [rooms, setRooms] = useState(1);

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    let ok = true;
    setLoading(true);
    axios
      .get(`${API}/packages/${slug}`)
      .then((res) => {
        if (!ok) return;
        setPkg(res.data);
        const rts = res.data.room_types || [];
        setRoomId(rts[0]?.id || null);
        setCheckOut(iso(addDays(new Date(), 21 + (res.data.nights || 5))));
      })
      .catch(() => ok && setNotFound(true))
      .finally(() => ok && setLoading(false));
    return () => {
      ok = false;
    };
  }, [slug]);

  const room = useMemo(
    () => (pkg?.room_types || []).find((r) => r.id === roomId) || (pkg?.room_types || [])[0],
    [pkg, roomId]
  );

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const diff = (new Date(checkOut) - new Date(checkIn)) / 86400000;
    return diff > 0 ? Math.round(diff) : 0;
  }, [checkIn, checkOut]);

  const total = room && nights > 0 ? room.price_per_night * nights * Math.max(1, rooms) : 0;
  const validDates = nights > 0;

  if (loading) return <PageLoader />;
  if (notFound || !pkg) return <Navigate to="/packages" replace />;

  const gallery = pkg.gallery && pkg.gallery.length ? pkg.gallery : [pkg.image];

  const startBooking = () => {
    if (!validDates) return;
    setCheckoutOpen(true);
  };

  const booking = {
    pkg,
    room,
    checkIn,
    checkOut,
    nights,
    rooms: Math.max(1, rooms),
    travelers: Math.max(1, travelers),
  };

  return (
    <>
      <SEO
        title={`${pkg.name} — ${pkg.destination}`}
        description={pkg.description?.slice(0, 155) || `Book ${pkg.name} in ${pkg.destination}.`}
        path={`/packages/${pkg.slug}`}
        image={pkg.image}
      />

      {/* HERO */}
      <section className="relative flex min-h-[56vh] items-end overflow-hidden">
        <img src={gallery[activeImg]} alt={pkg.alt} className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-12 pt-32 lg:px-8">
          <Link to="/packages" data-testid="pkg-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> All packages
          </Link>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-4">
            {pkg.tag && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">
                <Star className="h-3.5 w-3.5" /> {pkg.tag}
              </span>
            )}
            <h1 className="mt-4 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl">{pkg.name}</h1>
            <p className="mt-3 flex items-center gap-2 text-slate-200">
              <MapPin className="h-4 w-4" /> {pkg.location || pkg.destination}
            </p>
          </motion.div>
        </div>
      </section>

      {/* GALLERY THUMBS */}
      {gallery.length > 1 && (
        <section className="border-b border-slate-100 bg-white">
          <div className="mx-auto flex max-w-7xl gap-3 overflow-x-auto px-5 py-4 lg:px-8">
            {gallery.map((g, i) => (
              <button
                key={`${g}-${i}`}
                data-testid={`pkg-gallery-thumb-${i}`}
                onClick={() => setActiveImg(i)}
                className={`h-16 w-24 flex-none overflow-hidden rounded-xl ring-2 transition ${
                  activeImg === i ? "ring-primary" : "ring-transparent hover:ring-slate-200"
                }`}
              >
                <img src={g} alt={`${pkg.name} view ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-14 lg:grid-cols-3 lg:px-8">
        {/* LEFT: content */}
        <div className="lg:col-span-2">
          <Reveal>
            <Overline>The Resort</Overline>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy">About this escape</h2>
            <p className="mt-4 whitespace-pre-line text-lg leading-relaxed text-slate-600">{pkg.description}</p>
          </Reveal>

          {pkg.highlights?.length > 0 && (
            <Reveal delay={0.05}>
              <div className="mt-8 flex flex-wrap gap-3">
                {pkg.highlights.map((h) => (
                  <span key={h} className="inline-flex items-center gap-1.5 rounded-full bg-secondary/10 px-4 py-2 text-sm font-medium text-navy">
                    <Check className="h-4 w-4 text-secondary" /> {h}
                  </span>
                ))}
              </div>
            </Reveal>
          )}

          {pkg.amenities?.length > 0 && (
            <Reveal delay={0.05}>
              <h3 className="mt-12 font-display text-2xl font-bold text-navy">Resort amenities</h3>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {pkg.amenities.map((a) => (
                  <div key={a} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4 text-slate-600">
                    <Check className="h-5 w-5 flex-none text-secondary" /> {a}
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {/* ROOM TYPES */}
          <Reveal delay={0.05}>
            <h3 className="mt-12 font-display text-2xl font-bold text-navy" id="rooms">Choose your room</h3>
            <div className="mt-5 space-y-5">
              {(pkg.room_types || []).map((r) => {
                const selected = r.id === roomId;
                return (
                  <div
                    key={r.id}
                    data-testid={`pkg-room-${r.id}`}
                    className={`grid overflow-hidden rounded-3xl border-2 transition sm:grid-cols-[220px_1fr] ${
                      selected ? "border-primary shadow-md" : "border-slate-100"
                    }`}
                  >
                    <div className="relative h-44 sm:h-full">
                      <img src={r.image} alt={r.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="flex flex-col p-6">
                      <h4 className="font-display text-xl font-bold text-navy">{r.name}</h4>
                      <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{r.description}</p>
                      <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Up to {r.max_guests}</span>
                        <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" /> {r.size}</span>
                        <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" /> {r.beds}</span>
                      </div>
                      <div className="mt-4 flex items-end justify-between">
                        <p className="font-display text-xl font-bold text-navy">
                          {money(r.price_per_night)}<span className="text-sm font-normal text-slate-400"> / night</span>
                        </p>
                        <Button
                          data-testid={`pkg-room-select-${r.id}`}
                          onClick={() => setRoomId(r.id)}
                          className={`rounded-full px-6 ${selected ? "bg-secondary text-white" : "bg-navy text-white hover:bg-navy/90"}`}
                        >
                          {selected ? "Selected" : "Select"}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>

        {/* RIGHT: sticky booking panel */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 rounded-3xl border border-slate-100 bg-white p-6 shadow-lg" data-testid="pkg-booking-panel">
            <p className="text-sm text-slate-500">From</p>
            <p className="font-display text-3xl font-black text-navy">
              {money(pkg.price_from)}<span className="text-base font-normal text-slate-400"> / night</span>
            </p>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-navy">Check-in</Label>
                <Input
                  data-testid="pkg-checkin"
                  type="date"
                  min={iso(new Date())}
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-navy">Check-out</Label>
                <Input
                  data-testid="pkg-checkout"
                  type="date"
                  min={checkIn ? iso(addDays(new Date(checkIn), 1)) : iso(new Date())}
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-navy">Travelers</Label>
                <Input data-testid="pkg-travelers" type="number" min="1" max="30" value={travelers} onChange={(e) => setTravelers(e.target.value)} className="rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-navy">Rooms</Label>
                <Input data-testid="pkg-rooms" type="number" min="1" max="15" value={rooms} onChange={(e) => setRooms(e.target.value)} className="rounded-xl" />
              </div>
            </div>

            {/* Live summary */}
            <div className="mt-5 rounded-2xl bg-muted p-4 text-sm">
              <div className="flex items-center justify-between py-0.5 text-slate-500">
                <span>{room ? room.name : "Room"}</span>
                <span className="font-medium text-navy">{money(room?.price_per_night)}/nt</span>
              </div>
              <div className="flex items-center justify-between py-0.5 text-slate-500">
                <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {validDates ? `${nights} night(s) × ${Math.max(1, rooms)} room(s)` : "Pick valid dates"}</span>
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2 font-display text-lg font-bold text-navy">
                <span>Total</span>
                <span data-testid="pkg-total">{validDates ? money(total) : "—"}</span>
              </div>
              <p className="mt-1 text-xs text-slate-400">or {money((pkg.deposit || 0) * Math.max(1, rooms))} deposit to reserve</p>
            </div>

            <Button
              data-testid="pkg-book-button"
              onClick={startBooking}
              disabled={!validDates}
              className="mt-4 w-full rounded-full bg-primary py-6 text-base font-semibold text-primary-foreground btn-glow hover:bg-primary/90 disabled:opacity-50"
            >
              Book Now
            </Button>
            <button
              data-testid="pkg-inquire-button"
              onClick={() => setInquiryOpen(true)}
              className="mt-3 w-full text-sm font-semibold text-primary hover:underline"
            >
              or ask a question first
            </button>
          </div>
        </div>
      </div>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} booking={booking} />
      <InquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} pkg={pkg} />
    </>
  );
}
